import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SupportMessage {
  id: string;
  sender_type: "user" | "ia_suporte" | "ia_diagnostico" | "humano";
  content: string;
  created_at: string;
}

export interface SupportTicket {
  id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  priority: string;
  created_at: string;
  resolved_at?: string;
  sla_deadline?: string;
}

interface UseSupportOptions {
  userId?: string;
  userPlan?: string;
  currentPage?: string;
}

export function useSupport(options: UseSupportOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [currentTicketId, setCurrentTicketId] = useState<string | null>(null);
  const { toast } = useToast();

  const sendMessage = useCallback(async (
    message: string,
    mode: "chat" | "diagnostic" = "chat"
  ): Promise<{ response: string; needsEscalation: boolean }> => {
    setIsLoading(true);
    
    try {
      // Add user message to local state
      const userMessage: SupportMessage = {
        id: crypto.randomUUID(),
        sender_type: "user",
        content: message,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMessage]);

      const { data, error } = await supabase.functions.invoke("support-ai-agent", {
        body: {
          message,
          ticketId: currentTicketId,
          userId: options.userId,
          userPlan: options.userPlan,
          currentPage: options.currentPage,
          mode,
        },
      });

      if (error) throw error;

      // Add AI response to local state
      const aiMessage: SupportMessage = {
        id: crypto.randomUUID(),
        sender_type: mode === "diagnostic" ? "ia_diagnostico" : "ia_suporte",
        content: data.message,
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, aiMessage]);

      return {
        response: data.message,
        needsEscalation: data.needsEscalation || false,
      };
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Erro",
        description: "Não foi possível enviar sua mensagem. Tente novamente.",
        variant: "destructive",
      });
      return { response: "", needsEscalation: false };
    } finally {
      setIsLoading(false);
    }
  }, [currentTicketId, options, toast]);

  const createTicket = useCallback(async (
    title: string,
    description: string,
    category: string = "duvida_uso"
  ): Promise<string | null> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("support-create-ticket", {
        body: {
          userId: options.userId,
          title,
          description,
          category,
          userPlan: options.userPlan,
          currentPage: options.currentPage,
          userContext: {
            url: window.location.href,
            userAgent: navigator.userAgent,
          },
        },
      });

      if (error) throw error;

      setCurrentTicketId(data.ticketId);
      
      toast({
        title: "Chamado criado",
        description: "Seu chamado foi registrado com sucesso.",
      });

      return data.ticketId;
    } catch (error) {
      console.error("Error creating ticket:", error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o chamado. Tente novamente.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [options, toast]);

  const loadTicketMessages = useCallback(async (ticketId: string) => {
    try {
      const { data, error } = await supabase
        .from("support_messages")
        .select("*")
        .eq("ticket_id", ticketId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      setMessages((data || []).map(msg => ({
        ...msg,
        sender_type: msg.sender_type as SupportMessage["sender_type"],
      })));
      setCurrentTicketId(ticketId);
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  }, []);

  const loadUserTickets = useCallback(async (): Promise<SupportTicket[]> => {
    try {
      const { data, error } = await supabase
        .from("support_tickets")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error("Error loading tickets:", error);
      return [];
    }
  }, []);

  const resolveTicket = useCallback(async (
    ticketId: string,
    helpful: boolean
  ) => {
    try {
      await supabase.functions.invoke("support-resolve", {
        body: {
          ticketId,
          resolvedBy: "user",
          resolutionNotes: helpful ? "Usuário marcou como resolvido" : "Usuário encerrou sem resolução",
          createKnowledge: helpful,
        },
      });

      toast({
        title: "Chamado encerrado",
        description: helpful 
          ? "Obrigado pelo feedback! O Sentinela aprendeu com essa interação."
          : "Chamado encerrado. Se precisar, abra um novo.",
      });

      setCurrentTicketId(null);
      setMessages([]);
    } catch (error) {
      console.error("Error resolving ticket:", error);
    }
  }, [toast]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setCurrentTicketId(null);
  }, []);

  return {
    isLoading,
    messages,
    currentTicketId,
    sendMessage,
    createTicket,
    loadTicketMessages,
    loadUserTickets,
    resolveTicket,
    clearChat,
  };
}
